import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { useGetRegisterIdParam } from '@/modules/common/hooks/useGetRegisterIdParam.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { NOTIFICATIONS_LIST_QUERY_KEY } from '@/modules/issuer-dashboard/queries/event-logs.ts';
import { CURRENT_ISSUER_TRANSACTION_BY_ID_QUERY_KEY } from '@/modules/issuer-dashboard/queries/transactions.ts';
import { CURRENT_ISSUER_TRANSFER_BY_ID_QUERY_KEY } from '@/modules/issuer-dashboard/queries/transfers.tsx';
import {
  GetDocumentsResponse,
  GetDocumentsV1SrDocumentsGetParams,
  UpdateDocumentRequest,
} from 'api';

export const ISSUER_DOCUMENTS_QUERY_KEY = 'vestra-sr/issuer-documents-list';

export const useGetDocumentsQuery = (payload: GetDocumentsV1SrDocumentsGetParams) => {
  const { page } = payload;
  return useInfiniteQuery<GetDocumentsResponse>({
    queryKey: [ISSUER_DOCUMENTS_QUERY_KEY, JSON.stringify(payload), useGetRegisterIdParam()],
    staleTime: 1000 * 60 * 60, // 1 hour
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getDocumentsV1SrDocumentsGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    refetchInterval: 1000 * 60 * 5, //5min
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
  });
};

export const useUpdateDocumentsMutation = ({
  onSuccess,
}: { onSuccess?: (payload: UpdateDocumentRequest) => void } = {}) => {
  return useMutation({
    mutationFn: async (payload: UpdateDocumentRequest) => {
      await withErrorHandling(api.v1.updateDocumentsV1SrDocumentsPatch(payload), {
        loading: 'Updating documents',
        success: 'Documents successfully updated',
      });

      return payload;
    },
    onSuccess: async payload => {
      await queryClient.invalidateQueries([NOTIFICATIONS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_DOCUMENTS_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ISSUER_TRANSACTION_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ISSUER_TRANSFER_BY_ID_QUERY_KEY]);
      onSuccess && onSuccess(payload);
    },
  });
};
